"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface VapiAgentCursorProps {
  /** Whether the cursor is currently active */
  isActive?: boolean;
  /** Target field to point at - 'name', 'email', 'phone', or null */
  targetField?: 'name' | 'email' | 'phone' | null;
  /** Text to type after clicking */
  textToType?: string;
  /** Whether the cursor is currently clicking */
  isClicking?: boolean;
  /** Custom name label for the cursor */
  name?: string;
  /** Custom class for the cursor */
  className?: string;
}

// Predefined positions for each field (percentage based within container)
const FIELD_POSITIONS = {
  name: { x: 85, y: 28 },
  email: { x: 85, y: 46 },
  phone: { x: 85, y: 64 },
  default: { x: 50, y: 12 },
};

export function VapiAgentCursor({
  isActive = false,
  targetField = null,
  textToType = "",
  isClicking = false,
  name = "AYRA",
  className,
}: VapiAgentCursorProps) {
  const [currentPosition, setCurrentPosition] = React.useState(FIELD_POSITIONS.default);
  const [showClickRipple, setShowClickRipple] = React.useState(false);
  const [displayedText, setDisplayedText] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const [hasClicked, setHasClicked] = React.useState(false);

  // Animate to target field position
  React.useEffect(() => {
    if (targetField && FIELD_POSITIONS[targetField]) {
      setCurrentPosition(FIELD_POSITIONS[targetField]);
      setHasClicked(false);
      setDisplayedText("");
    } else if (!targetField) {
      setCurrentPosition(FIELD_POSITIONS.default);
      setHasClicked(false);
      setDisplayedText("");
    }
  }, [targetField]);

  // Handle click ripple effect
  React.useEffect(() => {
    if (isClicking && !hasClicked) {
      setShowClickRipple(true);
      setHasClicked(true);
      // Hide ripple after animation
      const timer = setTimeout(() => {
        setShowClickRipple(false);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isClicking, hasClicked]);

  // Typewriter effect AFTER clicking
  React.useEffect(() => {
    if (!textToType || !hasClicked) {
      return;
    }

    // Wait for click animation to finish, then start typing
    const startTypingTimer = setTimeout(() => {
      setIsTyping(true);
      let currentIndex = 0;
      setDisplayedText("");

      const typingInterval = setInterval(() => {
        if (currentIndex < textToType.length) {
          setDisplayedText(textToType.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 40); // Typing speed

      return () => clearInterval(typingInterval);
    }, 400); // Wait for click ripple to finish

    return () => clearTimeout(startTypingTimer);
  }, [textToType, hasClicked]);

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className={cn(
            "absolute z-[100] pointer-events-none",
            className
          )}
          initial={{ 
            opacity: 0, 
            scale: 0,
          }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            right: `${100 - currentPosition.x}%`,
            top: `${currentPosition.y}%`,
          }}
          exit={{ 
            opacity: 0, 
            scale: 0 
          }}
          transition={{
            type: "spring",
            stiffness: 120,
            damping: 18,
            mass: 0.8,
          }}
        >
          {/* Click Ripple Effect */}
          <AnimatePresence>
            {showClickRipple && (
              <>
                <motion.div
                  className="absolute top-0 left-0 w-8 h-8 rounded-full border-2 border-emerald-400"
                  initial={{ scale: 0.5, opacity: 1 }}
                  animate={{ scale: 2.5, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  style={{ transform: "translate(-50%, -50%)" }}
                />
                <motion.div
                  className="absolute top-0 left-0 w-6 h-6 rounded-full border-2 border-cyan-400"
                  initial={{ scale: 0.5, opacity: 1 }}
                  animate={{ scale: 2, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                  style={{ transform: "translate(-50%, -50%)" }}
                />
                <motion.div
                  className="absolute top-0 left-0 w-4 h-4 rounded-full bg-emerald-400/50"
                  initial={{ scale: 1, opacity: 1 }}
                  animate={{ scale: 0, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeIn" }}
                  style={{ transform: "translate(-50%, -50%)" }}
                />
              </>
            )}
          </AnimatePresence>

          {/* Cursor SVG - Green theme with press animation */}
          <motion.div
            animate={{
              scale: showClickRipple ? 0.85 : 1,
              rotate: isTyping ? [0, 2, -2, 0] : 0,
            }}
            transition={{
              scale: { duration: 0.15 },
              rotate: { duration: 0.3, repeat: isTyping ? Infinity : 0 },
            }}
          >
            <svg
              className="h-6 w-6 drop-shadow-lg filter"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              style={{ filter: "drop-shadow(0 2px 8px rgba(16, 185, 129, 0.5))" }}
            >
              <path
                d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.48 0 .72-.58.38-.91L5.97 2.93a.5.5 0 0 0-.47.28z"
                className="fill-emerald-500"
              />
              <path
                d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.48 0 .72-.58.38-.91L5.97 2.93a.5.5 0 0 0-.47.28z"
                className="fill-emerald-400"
                style={{ clipPath: "polygon(0 0, 100% 0, 100% 50%, 0 50%)" }}
              />
              <path
                d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.48 0 .72-.58.38-.91L5.97 2.93a.5.5 0 0 0-.47.28z"
                className="fill-none stroke-emerald-300"
                strokeWidth="0.5"
              />
            </svg>
          </motion.div>

          {/* Agent Name Badge */}
          <motion.div
            initial={{ opacity: 0, y: 5, x: 12 }}
            animate={{ opacity: 1, y: 0, x: 12 }}
            className="absolute top-4 left-0 flex items-center gap-1"
          >
            <div className="px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-[10px] font-semibold shadow-lg shadow-emerald-500/30 flex items-center gap-1">
              <motion.div
                className="w-1 h-1 rounded-full bg-white"
                animate={{
                  opacity: [1, 0.5, 1],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              {name}
            </div>
          </motion.div>

          {/* Typing Bubble - Only shows AFTER click */}
          <AnimatePresence>
            {hasClicked && displayedText && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                className="absolute top-10 left-6 max-w-[180px]"
              >
                <div className="relative">
                  <div className="absolute -top-1.5 left-3 w-3 h-3 bg-black/90 rotate-45 rounded-sm" />
                  <div className="relative bg-black/90 backdrop-blur-xl rounded-lg px-3 py-2 border border-emerald-500/30 shadow-xl shadow-emerald-500/10">
                    <p className="text-white text-xs font-medium leading-relaxed">
                      {displayedText}
                      {isTyping && (
                        <motion.span
                          className="inline-block w-0.5 h-3 bg-emerald-400 ml-0.5 align-middle"
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.4, repeat: Infinity }}
                        />
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Ambient glow when idle */}
          {!showClickRipple && !isTyping && (
            <motion.div
              className="absolute -top-2 -left-2 w-10 h-10 rounded-full border border-emerald-400/20"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.1, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
